import React, { useEffect, useRef, useState } from 'react';
import { FiLoader } from 'react-icons/fi';
import { classifyPdfError, loadPdfDocument, renderPdfPage } from '../../utils/pdfPreview';
import type { PdfErrorKind } from '../../utils/pdfPreview';

interface PdfPagePreviewProps {
  url: string;
  /** Width to rasterise at, in CSS pixels. */
  width?: number;
  /** Notifies the parent so it can fall back to the plain card. */
  onFailure?: (kind: PdfErrorKind) => void;
  /** Reports the page count once the document is parsed. */
  onLoaded?: (pageCount: number) => void;
}

/**
 * Renders page 1 of a PDF into the chat bubble, WhatsApp/Telegram style.
 * Rendering only starts once the bubble scrolls into view so long threads
 * don't rasterise every attachment at once.
 */
export const PdfPagePreview: React.FC<PdfPagePreviewProps> = ({
  url,
  width = 500,
  onFailure,
  onLoaded,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [status, setStatus] = useState<'idle' | 'loading' | 'done' | 'error'>('idle');
  const [visible, setVisible] = useState(false);

  // Defer work until the attachment is near the viewport.
  useEffect(() => {
    const node = containerRef.current;
    if (!node || visible) return;

    if (typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [visible]);

  useEffect(() => {
    if (!visible || !url) return;

    let cancelled = false;
    let task: { cancel: () => void } | null = null;
    setStatus('loading');

    (async () => {
      try {
        const doc = await loadPdfDocument(url);
        if (cancelled) return;

        onLoaded?.(doc.numPages);

        const canvas = canvasRef.current;
        if (!canvas) return;

        await renderPdfPage(doc, 1, canvas, width, {
          applyStyleSize: false,
          onTask: (t) => {
            task = t;
          },
        });
        if (!cancelled) setStatus('done');
      } catch (err) {
        // Cancelling a render rejects the promise; that isn't a real failure.
        if (cancelled) return;
        // CORS failures, password-protected or corrupt files land here —
        // the parent falls back to the standard document card.
        setStatus('error');
        onFailure?.(classifyPdfError(err));
      }
    })();

    return () => {
      cancelled = true;
      task?.cancel();
    };
    // onLoaded/onFailure are intentionally excluded; they are render-stable
    // callbacks and re-running would re-rasterise the page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, url, width]);

  if (status === 'error') return null;

  return (
    <div ref={containerRef} className="relative w-full h-36 sm:h-40 bg-white overflow-hidden">
      <canvas
        ref={canvasRef}
        className={`block w-full h-full object-cover object-top transition-opacity duration-300 ${
          status === 'done' ? 'opacity-100' : 'opacity-0'
        }`}
      />
      {status !== 'done' && (
        <div className="absolute inset-0 flex items-center justify-center bg-[#252528]">
          <FiLoader size={18} className="text-zinc-400 animate-spin" />
        </div>
      )}
    </div>
  );
};
